import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/CreateEvent.css";
import "../../css/GlobalStyles.css";
import { Helmet } from "react-helmet";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import {
  isEventTicketsPaid,
  isExternalEventSpecificLink,
  isSubscriptionPaymentFailed,
  getCanonicalMediaUrls,
} from "@markit/common.utils";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Event,
  EventType,
  MediaAspectRatio,
  OnboardingFlowType,
  OrganizerType,
} from "@markit/common.types";
import { useDispatch, useSelector } from "react-redux";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { firebaseStorage, listAll, ref } from "../../firebase";
import {
  filteredSampleImages,
  getImageAspectRatio,
} from "../../utils/photoUtils";
import { useCreateEventManagement } from "../../hooks/useCreateEventManagement";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { isDesktop } from "react-device-detect";
import EmptyViewStateMobile from "../../components/EmptyStates/EmptyViewStateMobile";
import FormBuilderWrapper from "../../components/Form/FormBuilderWrapper";
import { useEventSelector } from "../../hooks/useParametricSelector";
import { getUserTicket } from "../../utils/eventUtils/userTicketUtils";
import { eventActions, getEventState } from "../../redux/slices/eventSlice";
import { API } from "../../API";
import { DataLoaders } from "../../redux/slices/dataSlice";
import CreateEventTickets from "../../components/CreateEvent/CreateEventTickets";
import { getEventFormQuestions } from "../../utils/eventUtils/formUtils";
import LoadingScreen from "../LoadingScreen";
import { MixpanelContext } from "../../context/AnalyticsService";
import PaymentFailedButtonModal from "../../components/DisplayModal/UpdatePaymentInfoModal";
import EnablePaidTicketsModal from "../../components/DisplayModal/EnablePaidTicketsModal";
import ConfirmDeleteModal from "../../components/Containers/ConfirmPopups/ConfirmDeleteModal";
import CreateEventExtraDetails from "../../components/CreateEvent/CreateEventExtraDetails";
import CreateEventTitleDescriptionForm from "../../components/CreateEvent/CreateEventTitleDescriptionForm";
import RectangleButton from "../../components/Buttons/RectangleButton";
import StandardContainer from "../../components/Containers/StandardContainer";
import {
  NavigationId,
  NewProductNavigationId,
} from "../../navigation/AppParamList";
import CreateImageModal from "../../components/CreateEvent/CreateModals/CreateImageModal";
import AlertContainer from "../../components/Containers/AlertContainer";
import FullScreenContainer from "../../components/Containers/FullScreenContainer";
import MediaCarouselContainer from "../../components/Media/MediaCarouselContainer";
import MediaPreviewCarousel from "../../components/Media/MediaPreviewCarousel";
import MediaAspectRatioDropdown from "../../components/CreatorDashboard/Products/Components/MediaAspectRatioDropdown";
import OnboardingTourStep from "../../components/Onboarding/OnboardingTourStep";
import { ORGANIZE_EVENTS_ONBOARDING_STEPS } from "../../utils/onboardingFlowUtils";

export enum TicketingOptions {
  FREE = "Free",
  PAID = "Paid",
  REQUEST = "Request",
  CUSTOM = "Custom",
}

export const CreateEvent = () => {
  const { account } = useSelector(getAccountState);
  const {
    accountData,
    loggedIn,
    savedQuestions,
    readyForPayment,
    appInitialized,
  } = account;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation() || {};

  const {
    duplicatedEvent, // used for passing in the external event data too
    editEvent,
    externalNotScraped,
    isConvertingExternalEvent,
    cloneDetails,
  } = useLocation().state || {};

  // Only used for anyone that refreshes or goes straight to edit url
  const { events } = useSelector(getEventState);
  const eventId = useEventSelector();

  const existingEvent: Event | undefined = useMemo(
    () =>
      duplicatedEvent ??
      editEvent ??
      events.events.find((event) => event.id === eventId),
    [duplicatedEvent, editEvent, eventId, events.events]
  );

  // Modals for setting some event details
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);
  const [sampleEventPhotos, setSampleEventPhotos] = useState<string[]>([]);

  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string>("");
  const [paymentFailedVisible, setPaymentFailedVisible] = useState(false);
  const [connectStripeModal, setConnectStripeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [postingDraft, setPostingDraft] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [organizeEventsTourStep, setOrganizeEventsTourStep] = useState(2);

  const isWebinarType = useMemo(
    () =>
      pathname.includes(NewProductNavigationId.WEBINAR) ||
      (editEvent && editEvent.eventType === EventType.WEBINAR),
    [editEvent, pathname]
  );

  const productLabel = useMemo(
    () => (isWebinarType ? "Webinar" : "Event"),
    [isWebinarType]
  );

  const {
    updateEventSettings,
    postingEvent,
    isEditing,
    eventSettings,
    showError,
    onPostEventPress,
    onSaveEventPress,
    onNextExternalPress,
    onDraftEventPress,
  } = useCreateEventManagement({
    event: existingEvent,
    isEditing: editEvent || (eventId !== "" && !duplicatedEvent),
    cloneDetails: cloneDetails,
    setAlertText,
    type: isWebinarType ? EventType.WEBINAR : EventType.MARKIT,
  });

  const { theme } = useTheme(eventSettings.theme);

  const styles = {
    borderButton: {
      padding: 14,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      whiteSpace: "nowrap",
    },
    leftContainer: {
      width: 362,
      gap: 20,
    },
    rightContainer: {
      width: 508,
      gap: 24,
    },
    mediaFrame: {
      width: 362,
      aspectRatio: eventSettings.mediaAspectRatio || MediaAspectRatio._1_1,
      borderRadius: 20,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      overflow: "hidden",
      position: "relative" as const,
      flexShrink: 0,
      cursor: "pointer",
    },
    overlayButtonContainer: {
      top: 10,
      right: 10,
      zIndex: 10,
    },
  };

  const mediaUrls = useMemo(
    () => getCanonicalMediaUrls(eventSettings),
    [eventSettings]
  );

  const essentialQuestion = useMemo(
    () => savedQuestions.find((question) => question.isEssential),
    [savedQuestions]
  );

  const editingLiveEvent = useMemo(
    () => existingEvent && isEditing && !existingEvent.isDraft,
    [existingEvent, isEditing]
  );

  const eventReadyForPayment = useMemo(
    () =>
      eventSettings.stripePaymentCollector !== "" ? true : readyForPayment,
    [eventSettings.stripePaymentCollector, readyForPayment]
  );

  // External event that isn't being converted to a Markit event
  const isExternalLink = useMemo(
    () =>
      eventSettings.externalLink !== "" &&
      isExternalEventSpecificLink(eventSettings.eventType),
    [eventSettings.eventType, eventSettings.externalLink]
  );

  // Check if the subscription is Payment Failed
  useEffect(() => {
    if (isSubscriptionPaymentFailed(accountData.customer.state)) {
      setPaymentFailedVisible(true);
    }
  }, [accountData.customer.state]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (loggedIn === LoginState.LOGGED_IN && !appInitialized) {
      return;
    }
    if (loggedIn !== LoginState.LOGGED_IN) {
      navigate(NavigationId.WELCOME);
    }

    if (isEditing && existingEvent) {
      const userTicket = await getUserTicket(existingEvent.id, accountData.uid);
      if (
        loggedIn !== LoginState.LOGGED_IN ||
        !userTicket ||
        (userTicket &&
          userTicket.role.type !== OrganizerType.HOST &&
          userTicket.role.type !== OrganizerType.COHOST)
      ) {
        navigate("/e/" + existingEvent.id);
      }
    }
  }, [
    accountData.uid,
    appInitialized,
    dispatch,
    existingEvent,
    isEditing,
    loggedIn,
    mixpanel,
    navigate,
  ]);

  useAsyncOnMount(async () => {
    const utmCampaign = searchParams.get("utm_campaign");
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmContent = searchParams.get("utm_content");
    const utmTerm = searchParams.get("utm_term");

    // trigger creation mixpanel event
    mixpanel.track("Webapp: Visited Create Event", {
      markit_utm_campaign: utmCampaign,
      markit_utm_source: utmSource,
      markit_utm_medium: utmMedium,
      markit_utm_content: utmContent,
      markit_utm_termn: utmTerm,
    });

    // Initialize the event timezone, if not set
    if (!eventSettings.timezone) {
      updateEventSettings({
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    }

    // set the form questions
    if (existingEvent && !isExternalLink) {
      const formQuestions = await getEventFormQuestions(existingEvent);
      dispatch(accountActions.updateCurrentEventFormQuestions(formQuestions));
    }

    const photosRef = ref(firebaseStorage, "sampleEventPhotos/");
    await listAll(photosRef).then(async (listResult) => {
      const filteredImages = await filteredSampleImages(listResult);
      setSampleEventPhotos(filteredImages);
      if (!isEditing) {
        // Set the default image if:
        // 1. External event converting with no data scraped
        // 2. Markit event creation
        if (
          externalNotScraped ||
          (!isConvertingExternalEvent &&
            eventSettings.eventType === EventType.MARKIT &&
            !cloneDetails)
        ) {
          const randomImage =
            filteredImages[Math.floor(Math.random() * filteredImages.length)];
          const randomImageAspectRatio =
            (await getImageAspectRatio(randomImage)) || MediaAspectRatio._1_1;
          updateEventSettings({
            mediaURLs: [randomImage],
            mediaAspectRatio: randomImageAspectRatio,
          });
        }
      }
    });
    setLoading(false);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // add the essential question by default to event
    // Don't add if editing event and is Markit event
    if (
      loggedIn === LoginState.LOGGED_IN &&
      essentialQuestion &&
      !isEditing &&
      eventSettings.eventType === EventType.MARKIT
    ) {
      dispatch(
        accountActions.updateCurrentEventFormQuestions([essentialQuestion])
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [essentialQuestion]);

  const deleteDraftOnPress = useCallback(async () => {
    if (existingEvent) {
      dispatch(eventActions.removeEvent(existingEvent.id));
      await API.events.deleteEvent({
        eventId: existingEvent.id,
      });
      setIsDeleteDraftModalOpen(false);
      if (accountData.inCreatorMode) {
        navigate(NavigationId.HOME_PRODUCTS);
      } else {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      }
    }
  }, [
    accountData.inCreatorMode,
    accountData.uid,
    accountData.username,
    dispatch,
    existingEvent,
    navigate,
  ]);

  const finishOnPress = useCallback(async () => {
    if (!eventReadyForPayment && isEventTicketsPaid(eventSettings)) {
      // check if readyForPayment on a paid ticketed event
      setConnectStripeModal(true);
    } else if (isExternalLink) {
      // If editing external event or filling in non-scraped external event details
      onNextExternalPress();
    } else {
      // Regular Markit event
      if (isEditing) {
        await onSaveEventPress();
      } else {
        await onPostEventPress();
      }
    }
  }, [
    eventReadyForPayment,
    eventSettings,
    isEditing,
    isExternalLink,
    onNextExternalPress,
    onPostEventPress,
    onSaveEventPress,
  ]);

  const backOnPress = useCallback(() => {
    switch (eventSettings.eventType) {
      case EventType.MARKIT:
      case EventType.WEBINAR:
        navigate(-1);
        break;
      case EventType.EXTERNAL_DATA:
      case EventType.EXTERNAL_NO_DATA:
        if (isEditing) {
          navigate(`/e/${eventSettings.id}/editLink`, {
            state: {
              confirmState: true,
              event: existingEvent,
            },
          });
        } else {
          navigate(NewProductNavigationId.LINK_EVENT, {
            state: externalNotScraped
              ? undefined
              : {
                  confirmState: true,
                  event: existingEvent,
                },
          });
        }
        break;
      default:
        navigate(-1);
    }
  }, [
    eventSettings.eventType,
    eventSettings.id,
    existingEvent,
    externalNotScraped,
    isEditing,
    navigate,
  ]);

  const removeMedia = useCallback(
    (index: number) => {
      if (mediaUrls.length === 1) {
        setAlertText("You must have at least one image.");
        return;
      }
      updateEventSettings({
        mediaURLs: mediaUrls.filter((_, i) => i !== index),
      });
      const newLength = mediaUrls.length - 1;
      setSelectedMediaIndex((i) => {
        if (i === index) {
          return Math.min(index, newLength - 1);
        }
        if (index < i) {
          return i - 1;
        }
        return i;
      });
    },
    [mediaUrls, updateEventSettings]
  );

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isDesktop) {
    return <EmptyViewStateMobile eventTheme={eventSettings.theme} />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Helmet>
        <title>{`Create Markit ${productLabel}`}</title>
        <meta name="og:title" content={`Create Markit ${productLabel}`} />
        <meta name="og:description" content={`Create Markit ${productLabel}`} />
      </Helmet>
      <FullScreenContainer
        title={
          isExternalLink
            ? "Create"
            : isEditing
              ? `Edit ${productLabel}`
              : `Create ${productLabel}`
        }
        helmetTitle={
          isExternalLink
            ? "Edit"
            : isEditing
              ? `Edit ${productLabel}`
              : `Create ${productLabel}`
        }
        backOnPress={backOnPress}
        alternateLeftIcon="ion:arrow-back"
        theme={theme}
        rightHeaderComp={
          <div className="AlignedRow" style={{ gap: 10 }}>
            {!isExternalLink ? (
              (!isEditing || (existingEvent && existingEvent.isDraft)) &&
              loggedIn === LoginState.LOGGED_IN ? (
                <>
                  {eventSettings.isDraft ? (
                    <RectangleButton
                      buttonLabel="Delete Draft"
                      onPress={() =>
                        isEditing && setIsDeleteDraftModalOpen(true)
                      }
                      altColor={theme.NewPrimaryBG.backgroundColor}
                      altTextColor={theme.PrimaryText.color}
                      containerStyles={styles.borderButton}
                      disabled={postingDraft || postingEvent}
                    />
                  ) : null}
                  <RectangleButton
                    buttonLabel={eventSettings.isDraft ? "Save" : "Save Draft"}
                    onPress={async () => {
                      setPostingDraft(true);
                      await onDraftEventPress(!isEditing);
                    }}
                    altColor={theme.NewPrimaryBG.backgroundColor}
                    altTextColor={theme.PrimaryText.color}
                    containerStyles={styles.borderButton}
                    loading={postingEvent || postingDraft}
                  />
                </>
              ) : null
            ) : null}
            <div className="onboarding-create-event-publish-target">
              <RectangleButton
                buttonLabel={
                  editingLiveEvent || (isExternalLink && !externalNotScraped)
                    ? "Save"
                    : externalNotScraped && !isConvertingExternalEvent
                      ? "Next"
                      : "Publish"
                }
                altColor={theme.ButtonBG.backgroundColor}
                altTextColor={theme.ButtonText.color}
                onPress={finishOnPress}
                disabled={postingDraft}
                loading={postingEvent}
                containerStyles={{ padding: 14 }}
              />
            </div>
          </div>
        }
      >
        <div
          className="RowNormal"
          style={{ justifyContent: "center", gap: 30, paddingBottom: 60 }}
        >
          <div className="ColumnNormal" style={styles.leftContainer}>
            <div className="ColumnNormal" style={{ gap: 14 }}>
              {mediaUrls.length > 0 ? (
                <div style={styles.mediaFrame}>
                  <MediaCarouselContainer
                    event={eventSettings}
                    selectedMediaIndex={selectedMediaIndex}
                    setSelectedMediaIndex={setSelectedMediaIndex}
                    onImageClick={() => {
                      setIsImageModalOpen(true);
                      setSelectedMediaUrl(mediaUrls[selectedMediaIndex]);
                    }}
                    imageStyle={{
                      backgroundColor: theme.SubComponentBG.backgroundColor,
                    }}
                  />
                  <div
                    style={{
                      ...styles.overlayButtonContainer,
                      position: "absolute",
                    }}
                  >
                    <MediaAspectRatioDropdown
                      productData={eventSettings}
                      setProductData={updateEventSettings}
                    />
                  </div>
                </div>
              ) : (
                <div
                  className="Centering"
                  style={{
                    ...styles.mediaFrame,
                    border:
                      showError && mediaUrls.length === 0
                        ? "0.5px solid #DF4840"
                        : `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                    ...theme.NewPrimaryBG,
                  }}
                  onClick={() => setIsImageModalOpen(true)}
                >
                  <div
                    style={{
                      display: "grid",
                      placeItems: "center",
                      opacity: 0.3,
                    }}
                  >
                    <Icon
                      icon={"mdi:image-outline"}
                      style={{ color: Colors.GRAY1 }}
                      height={48}
                    />
                    <span
                      className="AboutSubtitle"
                      style={{ textAlign: "center", ...theme.PrimaryText }}
                    >
                      Select {productLabel} Photo
                    </span>
                  </div>
                </div>
              )}
              {mediaUrls.length > 0 ? (
                <MediaPreviewCarousel
                  event={eventSettings}
                  hardTheme={eventSettings.theme}
                  onAddMedia={() => setIsImageModalOpen(true)}
                  onSelectMedia={setSelectedMediaIndex}
                  onRemoveMedia={removeMedia}
                  onReorderMedia={(newUrls) =>
                    updateEventSettings({ mediaURLs: newUrls })
                  }
                />
              ) : null}
            </div>
            <CreateEventExtraDetails
              eventSettings={eventSettings}
              updateEventSettings={updateEventSettings}
            />
          </div>
          <div className="ColumnNormal" style={styles.rightContainer}>
            {!isEditing && eventSettings.externalLink !== "" ? (
              <StandardContainer
                containerStyles={{
                  ...theme.NewPrimaryBG,
                  border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                  margin: 0,
                }}
                headerComp={
                  <div className="AlignedRowSpaced" style={{ width: "100%" }}>
                    <div className="ColumnNormal" style={{ gap: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: "500" }}>
                        Event Info
                      </span>
                      <span style={{ fontSize: 12, ...theme.SubText }}>
                        {!externalNotScraped
                          ? "We've autofilled your details based on your link."
                          : "Fill in your event details."}
                      </span>
                    </div>
                    {!externalNotScraped ? (
                      <Icon
                        icon={"ion:checkmark-circle"}
                        height={18}
                        style={{ color: Colors.GREEN2 }}
                      />
                    ) : null}
                  </div>
                }
                theme={theme}
              />
            ) : null}
            {/* Event Title & Description */}
            <div className="onboarding-create-event-info-target">
              <CreateEventTitleDescriptionForm
                eventSettings={eventSettings}
                updateEventSettings={updateEventSettings}
                showError={showError}
                isEditing={isEditing}
              />
            </div>
            {!isEditing ? (
              organizeEventsTourStep === 2 ? (
                <OnboardingTourStep
                  key="create-event-tour-step-2"
                  flowType={OnboardingFlowType.ORGANIZE_EVENTS}
                  target=".onboarding-create-event-info-target"
                  content="Fill out information about your event here! Start with the title, date, and location."
                  stepNumber={2}
                  totalSteps={ORGANIZE_EVENTS_ONBOARDING_STEPS}
                  placement="right"
                  onStepComplete={() => setOrganizeEventsTourStep(3)}
                />
              ) : organizeEventsTourStep === 3 ? (
                <OnboardingTourStep
                  key="create-event-tour-step-3"
                  flowType={OnboardingFlowType.ORGANIZE_EVENTS}
                  target=".onboarding-create-event-ticketing-target"
                  content={
                    <>
                      Add or edit ticket types, each with its own price,
                      quantity available, and other settings.
                      {!readyForPayment ? (
                        <>
                          <br />
                          <br />
                          If you plan to charge for tickets, you&apos;ll need to
                          connect Stripe first.
                        </>
                      ) : null}
                    </>
                  }
                  stepNumber={3}
                  totalSteps={ORGANIZE_EVENTS_ONBOARDING_STEPS}
                  placement="right"
                  onStepComplete={() => setOrganizeEventsTourStep(4)}
                />
              ) : organizeEventsTourStep === 4 ? (
                <OnboardingTourStep
                  key="create-event-tour-step-4"
                  flowType={OnboardingFlowType.ORGANIZE_EVENTS}
                  target=".onboarding-create-event-questions-target"
                  content="Add questions to collect additional information before people can register for your event."
                  stepNumber={4}
                  totalSteps={ORGANIZE_EVENTS_ONBOARDING_STEPS}
                  placement="right"
                  onStepComplete={() => setOrganizeEventsTourStep(5)}
                />
              ) : (
                <OnboardingTourStep
                  key="create-event-tour-step-5"
                  flowType={OnboardingFlowType.ORGANIZE_EVENTS}
                  target=".onboarding-create-event-publish-target"
                  content={
                    <>
                      A text automation will be created automatically upon
                      publishing, which you can customize from the event
                      dashboard.
                    </>
                  }
                  stepNumber={5}
                  totalSteps={ORGANIZE_EVENTS_ONBOARDING_STEPS}
                  placement="bottom"
                />
              )
            ) : null}
            {/* Ticketing Box */}
            {eventSettings.eventType === EventType.MARKIT ||
            eventSettings.eventType === EventType.WEBINAR ? (
              <>
                <div className="onboarding-create-event-ticketing-target">
                  <CreateEventTickets
                    eventSettings={eventSettings}
                    updateEventSettings={updateEventSettings}
                    showConnectStripeModal={() => setConnectStripeModal(true)}
                    readyForPayment={readyForPayment}
                    hardTheme={eventSettings.theme}
                  />
                </div>
                <div className="onboarding-create-event-questions-target">
                  <FormBuilderWrapper
                    isProfileQuestions={false}
                    hostId={eventSettings.createdBy}
                    event={eventSettings}
                    updateEventSettings={updateEventSettings}
                    themeType={eventSettings.theme}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
        {/* Modal for setting event image */}
        {isImageModalOpen ? (
          <CreateImageModal
            eventSettings={eventSettings}
            updateEventSettings={updateEventSettings}
            setIsImageModalOpen={setIsImageModalOpen}
            selectedImage={selectedMediaUrl}
            setSelectedImage={setSelectedMediaUrl}
            sampleEventPhotos={sampleEventPhotos}
            multipleMedia
          />
        ) : null}
        <ConfirmDeleteModal
          heading="Are you sure you want to delete this event draft?"
          subtext="You cannot undo this."
          deleteButtonText="Delete"
          hideModal={!isDeleteDraftModalOpen}
          setIsVisible={setIsDeleteDraftModalOpen}
          deleteOnPress={deleteDraftOnPress}
          theme={theme}
        />
        {paymentFailedVisible ? (
          <PaymentFailedButtonModal
            location={`Create ${productLabel}`}
            isVisible={paymentFailedVisible}
            setIsVisible={() => {
              setPaymentFailedVisible(false);
            }}
            addOnClose={() => navigate("/home")}
          />
        ) : null}
        {connectStripeModal ? (
          <EnablePaidTicketsModal
            isVisible={connectStripeModal}
            setIsVisible={setConnectStripeModal}
            theme={theme}
          />
        ) : null}
        <AlertContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <Icon
                icon="ion:close-circle"
                height={40}
                style={{ color: Colors.RED3 }}
              />
              <span style={{ fontSize: 16, fontWeight: "500" }}>
                {alertText}
              </span>
            </div>
          }
          theme={theme}
          closeModal={() => setAlertText("")}
          hideModal={alertText === ""}
        />
      </FullScreenContainer>
    </LocalizationProvider>
  );
};
